import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import { rampsActions } from "../stores/use-ramps-store";

/**
 * Feature Flag Configuration
 * Add new flags here as they're created in PostHog
 */
export const FEATURE_FLAGS = {
	RAMPS_MIN_AMOUNT_CALCULATOR: "ramps-min-amount-calculator-page",
	RAMPS_MAX_AMOUNT_CALCULATOR: "ramps-max-amount-calculator-page",
	RAMPS_MAX_SEND_LIMIT: "ramps-max-send-limit",
	RAMPS_ADDRESS_LOOKUP_ENABLED: "ramps-address-lookup-enabled",
	RAMPS_KYC_POLLING_TIME: "ramps-kyc-polling-time",
	RAMPS_KYC_FLOW_ENABLED: "ramps-kyc-flow-enabled",
	ENABLE_DIRECT_TO_ACCT: "ramps-enable-direct-to-account-feature",
	RAMPS_CASH_IN_FLOW_ENABLED: "ramps-cash-in-flow-enabled",
	// A/B Experiment: Controls which calculator UI variant is shown
	RAMPS_NEW_CALCULATOR_PAGE_ENABLED: "ramps-new-calculator-page-enabled",
	// Wallet partners that should skip geolocation (e.g. WkWebView without location support)
	RAMPS_SKIP_GEOLOCATION_PARTNERS: "ramps-skip-geolocation-partners",
	// Gate postMessage to parent window on review commit (default false)
	RAMPS_REVIEW_POSTMESSAGE_ENABLED: "ramps-review-postmessage-enabled",
	// Gate legacy SEP-24-shaped postMessage (opener/parent) on review commit (default false)
	RAMPS_REVIEW_LEGACY_POSTMESSAGE_ENABLED:
		"ramps-review-legacy-postmessage-enabled",
	// Maintenance page overrides per flow (independent of isServerDown API flag)
	RAMPS_CASHOUT_MAINTENANCE: "ramps-cashout-maintenance",
	RAMPS_CASHIN_MAINTENANCE: "ramps-cashin-maintenance",
	// Remotely-configurable app support email shown in the "Need help?" section.
	// Payload: { email: string }. Falls back to DEFAULT_SUPPORT_EMAIL when disabled.
	RAMPS_SUPPORT_EMAIL: "ramps-support-email",
} as const;

/**
 * Type-safe feature flag payloads
 * Define the expected payload shape for each flag
 */
export interface FeatureFlagPayloads {
	[FEATURE_FLAGS.RAMPS_MIN_AMOUNT_CALCULATOR]: {
		MIN_AMOUNT?: number | string; // PostHog might send uppercase
		enabled?: boolean;
	};
	[FEATURE_FLAGS.RAMPS_MAX_AMOUNT_CALCULATOR]: {
		MAX_AMOUNT?: number | string; // PostHog might send uppercase
		enabled?: boolean;
	};
	[FEATURE_FLAGS.RAMPS_MAX_SEND_LIMIT]: {
		MAX_SEND_LIMIT?: number | string; // Maximum send limit in USDC
		enabled?: boolean;
	};
	[FEATURE_FLAGS.RAMPS_KYC_POLLING_TIME]: {
		POLLING_TIME?: number | string; // PostHog might send uppercase
		enabled?: boolean;
	};
	[FEATURE_FLAGS.ENABLE_DIRECT_TO_ACCT]: {
		enabled?: boolean;
		supportedCountries?: string[]; // Array of ISO3 country codes (e.g., ["COL", "MEX"])
	};
	[FEATURE_FLAGS.RAMPS_SKIP_GEOLOCATION_PARTNERS]: {
		partners?: string[]; // e.g. ["lobstr", "some_other_partner"]
	};
	[FEATURE_FLAGS.RAMPS_SUPPORT_EMAIL]: {
		email?: string; // e.g. "digitalsupport@moneygram.com"
	};
	// Add more payload types here
	// [FEATURE_FLAGS.EXAMPLE_FLAG]: {
	//   someValue?: string
	// }
}

/**
 * Custom hook to manage PostHog feature flags
 * Handles loading, checking, and accessing feature flag payloads
 */
export function useFeatureFlags() {
	const posthog = usePostHog();
	const [flagsLoaded, setFlagsLoaded] = useState(false);
	const [flags, setFlags] = useState<Record<string, boolean>>({});

	useEffect(() => {
		/** Apply safe defaults so the entire Ramps flow works without PostHog. */
		const applySafeDefaults = () => {
			setFlagsLoaded(true);
			rampsActions.setMinAmount(15);
			rampsActions.setMaxAmount(10_000);
			rampsActions.setMaxSendLimit(10_000);
			rampsActions.setKycFlowEnabled(true);
			rampsActions.setCashInFlowEnabled(true);
		};

		if (!posthog) {
			applySafeDefaults();
			return;
		}

		let didFallback = false;

		// Fallback: if feature flags never load within 5 s, use defaults
		const timeout = setTimeout(() => {
			if (!didFallback) {
				didFallback = true;
				applySafeDefaults();
			}
		}, 5_000);

		const applyFlags = () => {
			try {
				if (didFallback) return;
				didFallback = true;
				clearTimeout(timeout);
				setFlagsLoaded(true);

				// Check all flags and store their enabled state
				const flagStates: Record<string, boolean> = {};

				for (const flagKey of Object.values(FEATURE_FLAGS)) {
					const isEnabled = posthog.isFeatureEnabled(flagKey) ?? false;
					flagStates[flagKey] = isEnabled;
				}
				setFlags(flagStates);

				// Handle min amount calculator flag
				const isMinAmountEnabled = posthog.isFeatureEnabled(
					FEATURE_FLAGS.RAMPS_MIN_AMOUNT_CALCULATOR,
				);

				if (isMinAmountEnabled) {
					const payload = posthog.getFeatureFlagPayload(
						FEATURE_FLAGS.RAMPS_MIN_AMOUNT_CALCULATOR,
					) as FeatureFlagPayloads[typeof FEATURE_FLAGS.RAMPS_MIN_AMOUNT_CALCULATOR];

					const minAmountValue = payload?.MIN_AMOUNT;

					if (minAmountValue) {
						const minAmount =
							typeof minAmountValue === "string"
								? Number.parseFloat(minAmountValue)
								: minAmountValue;

						if (!Number.isNaN(minAmount) && minAmount > 0) {
							rampsActions.setMinAmount(minAmount);
						}
					}
				} else {
					rampsActions.setMinAmount(15);
				}

				// Handle max amount calculator flag
				const isMaxAmountEnabled = posthog.isFeatureEnabled(
					FEATURE_FLAGS.RAMPS_MAX_AMOUNT_CALCULATOR,
				);

				if (isMaxAmountEnabled) {
					const payload = posthog.getFeatureFlagPayload(
						FEATURE_FLAGS.RAMPS_MAX_AMOUNT_CALCULATOR,
					) as FeatureFlagPayloads[typeof FEATURE_FLAGS.RAMPS_MAX_AMOUNT_CALCULATOR];

					const maxAmountValue = payload?.MAX_AMOUNT;

					if (maxAmountValue) {
						const maxAmount =
							typeof maxAmountValue === "string"
								? Number.parseFloat(maxAmountValue)
								: maxAmountValue;

						if (!Number.isNaN(maxAmount) && maxAmount > 0) {
							rampsActions.setMaxAmount(maxAmount);
						}
					}
				}

				// Handle max send limit flag
				const isMaxSendLimitEnabled = posthog.isFeatureEnabled(
					FEATURE_FLAGS.RAMPS_MAX_SEND_LIMIT,
				);

				if (isMaxSendLimitEnabled) {
					const payload = posthog.getFeatureFlagPayload(
						FEATURE_FLAGS.RAMPS_MAX_SEND_LIMIT,
					) as FeatureFlagPayloads[typeof FEATURE_FLAGS.RAMPS_MAX_SEND_LIMIT];

					const maxSendLimitValue = payload?.MAX_SEND_LIMIT;

					if (maxSendLimitValue) {
						const maxSendLimit =
							typeof maxSendLimitValue === "string"
								? Number.parseFloat(maxSendLimitValue)
								: maxSendLimitValue;

						if (!Number.isNaN(maxSendLimit) && maxSendLimit > 0) {
							rampsActions.setMaxSendLimit(maxSendLimit);
						}
					}
				}

				// Handle KYC polling time flag
				const isKycPollingTimeEnabled = posthog.isFeatureEnabled(
					FEATURE_FLAGS.RAMPS_KYC_POLLING_TIME,
				);

				const payload = posthog.getFeatureFlagPayload(
					FEATURE_FLAGS.RAMPS_KYC_POLLING_TIME,
				) as FeatureFlagPayloads[typeof FEATURE_FLAGS.RAMPS_KYC_POLLING_TIME];

				if (isKycPollingTimeEnabled) {
					const pollingTimeValue = payload?.POLLING_TIME;

					if (pollingTimeValue) {
						const pollingTime =
							typeof pollingTimeValue === "string"
								? Number.parseFloat(pollingTimeValue)
								: pollingTimeValue;

						if (!Number.isNaN(pollingTime) && pollingTime > 0) {
							rampsActions.setKycPollingTime(pollingTime);
						}
					}
				}

				// Handle KYC flow enabled flag
				const isKycFlowEnabled = posthog.isFeatureEnabled(
					FEATURE_FLAGS.RAMPS_KYC_FLOW_ENABLED,
				);
				rampsActions.setKycFlowEnabled(isKycFlowEnabled ?? false);

				// Handle cash in (deposit) flow enabled flag
				const isCashInFlowEnabled = posthog.isFeatureEnabled(
					FEATURE_FLAGS.RAMPS_CASH_IN_FLOW_ENABLED,
				);
				rampsActions.setCashInFlowEnabled(isCashInFlowEnabled ?? true);
			} catch {
				// Feature flag processing failed — apply safe defaults
				applySafeDefaults();
			}
		};

		posthog.onFeatureFlags(applyFlags);

		return () => {
			clearTimeout(timeout);
		};
	}, [posthog]);

	/**
	 * Check if a specific feature flag is enabled
	 */
	const isFeatureEnabled = (flagKey: string): boolean => {
		if (!posthog || !flagsLoaded) return false;
		return posthog.isFeatureEnabled(flagKey) ?? false;
	};

	/**
	 * Get the payload for a specific feature flag with type safety
	 */
	const getFeatureFlagPayload = <K extends keyof FeatureFlagPayloads>(
		flagKey: K,
	): FeatureFlagPayloads[K] | undefined => {
		if (!posthog || !flagsLoaded) return undefined;
		return posthog.getFeatureFlagPayload(flagKey) as FeatureFlagPayloads[K];
	};

	return {
		flagsLoaded,
		flags,
		isFeatureEnabled,
		getFeatureFlagPayload,
		posthog, // Direct access if needed
	};
}
